import { useParams, Link, useNavigate } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useToast } from '../hooks/useToast';
import { getContentId } from '../types';
import SectionToggle from '../components/SectionToggle';
import PageSettings from '../components/PageSettings';
import DraggableList from '../components/DraggableList';
import { useEditorPreview } from '../hooks/useEditorPreview';
import '../styles/editor.css';

export default function TestimonialList() {
  const { siteId } = useParams<{ siteId: string }>();
  const { items, loading, error, remove, reorder } = useContent(siteId, 'testimonial');
  const { addToast } = useToast();
  const navigate = useNavigate();
  useEditorPreview('/');

  const handleDelete = async (contentKey: string, name: string) => {
    if (!confirm(`Delete testimonial from "${name}"?`)) return;
    try {
      const id = getContentId(contentKey);
      await remove(id);
      addToast('success', 'Testimonial deleted');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleReorder = async (fromIndex: number, toIndex: number) => {
    const newItems = [...items];
    const [moved] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, moved);
    const ordered = newItems.map((item, i) => ({
      id: getContentId(item.contentKey),
      sortOrder: i,
    }));
    try {
      await reorder(ordered);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to reorder');
    }
  };

  const renderStars = (rating: string | undefined) => {
    const num = parseInt(rating || '0', 10);
    if (!num || num < 1 || num > 5) return null;
    return <span style={{ color: '#f5a623', letterSpacing: '1px' }}>{'★'.repeat(num)}{'☆'.repeat(5 - num)}</span>;
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Testimonials</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header"><h1>Testimonials</h1></div>
        <div className="error-state"><p>{error}</p></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Testimonials</h1>
        <div className="page-header-actions">
          <SectionToggle siteId={siteId!} field="showTestimonials" label="Testimonials" />
          <Link to={`/sites/${siteId}/content/testimonials/new`} className="btn btn-primary">
            + Add Testimonial
          </Link>
        </div>
      </div>

      <PageSettings siteId={siteId!} fields={['testimonialsTitle', 'testimonialsSubtitle', 'googleReviewsUrl']} />

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></div>
          <h2>No testimonials yet</h2>
          <p>Share what your customers say about you.</p>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-gray-500)', marginBottom: 16 }}>
            Reviews build trust and help new customers choose you.
          </p>
          <Link to={`/sites/${siteId}/content/testimonials/new`} className="btn btn-primary">
            + Add your first testimonial
          </Link>
        </div>
      ) : (
        <DraggableList
          items={items.map((item) => {
            const data = item.data as Record<string, string>;
            const contentId = getContentId(item.contentKey);
            return {
              key: item.contentKey,
              element: (
                <>
                  <div
                    className="list-item-content"
                    onClick={() =>
                      navigate(`/sites/${siteId}/content/testimonials/${contentId}`)
                    }
                  >
                    <h3 className="list-item-title">
                      {data.name || 'Anonymous'}
                      {data.featured === 'true' || data.featured === true as unknown as string
                        ? <span style={{ color: '#f5a623', fontSize: '0.8em', marginLeft: '0.5rem' }}>★ Featured</span>
                        : null}
                    </h3>
                    <div className="list-item-meta">
                      {renderStars(data.rating)}
                      {data.source && <span className="tag">{data.source}</span>}
                      {data.quote && (
                        <span className="list-item-price" style={{ fontStyle: 'italic' }}>
                          &ldquo;{data.quote.length > 80 ? data.quote.substring(0, 80) + '…' : data.quote}&rdquo;
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="list-item-actions">
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(item.contentKey, data.name || 'this testimonial')}
                    >
                      Delete
                    </button>
                  </div>
                </>
              ),
            };
          })}
          onReorder={handleReorder}
        />
      )}
    </div>
  );
}
